// ./src/components/features/Accommodations/AccommodationEditDrawer/AccommodationEditDrawer.tsx
//
// Create/edit drawer for an event accommodation (a place to stay). Self-loading in
// edit mode via recordId; context-or-props resolution; no success toast (the page
// highlight is the confirmation). Fields: name, type (category), description
// (markdown), address, distance-from-venue, link, website, phone, email, price +
// currency + unit, group booking code, status, and — once saved — room photos.
// Mirrors PoiEditDrawer.

import React, { useState, useEffect, useCallback, useMemo, useRef, Suspense } from "react";
import { Stack, TextInput, NumberInput, Input, Skeleton, Button, Divider } from "@mantine/core";

import { useTranslation } from "react-i18next";
import { OpResult } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";

import { Accommodation, AccommodationInput, accommodationImageUrl, eventContextPath } from "@/utils/api";
import { getFormErrors, formatErrors, formatAddressFull, haversineMeters, formatDistanceFromMeters } from "@/utils";
import { useModalShortcuts, SHORTCUT_HINTS } from "@/hooks";
import { PlaceData, AddressData } from "@/hooks/useGoogleMapsAutocomplete";
import { useOptionalAccommodationsContext } from "@/contexts/AccommodationsContext";
import {
  AppDrawer,
  AppIcon,
  StatusInput,
  CategorySelect,
  useCurrencyAffixes,
  PlaceAutocompleteInput,
  AddressAutocompleteInput,
  MediaGalleryField,
  MediaImageRef,
} from "@/components/generic";

// Hotels / resorts / rentals are lookup-able establishments.
const ACCOMMODATION_PLACE_TYPES = ["establishment"];

const MarkdownField = React.lazy(() =>
  import("@/components/generic/MarkdownField/MarkdownField").then((m) => ({ default: m.MarkdownField }))
);

export interface AccommodationEditDrawerProps {
  opened: boolean;
  onClose: () => void;
  recordId?: string | null;
  /** The event this accommodation belongs to — set on the input in CREATE mode only. */
  eventId?: string | null;
  /** The event venue's coordinates, used to suggest the distance-from-venue. */
  venueLatitude?: number | null;
  venueLongitude?: number | null;
  onCreated?: (accommodation: Accommodation) => void;
  onUpdated?: (accommodation: Accommodation) => void;
}

interface FormData {
  categoryId: string | null;
  name: string;
  description: string;
  streetAddress: string;
  latitude: number | null;
  longitude: number | null;
  distanceKm: number | "";
  link: string;
  website: string;
  phone: string;
  email: string;
  price: number | "";
  priceUnit: string;
  groupBookingCode: string;
  photos: MediaImageRef[];
  status: number;
}

const KNOWN_FORM_FIELDS = [
  "categoryId",
  "name",
  "description",
  "streetAddress",
  "latitude",
  "longitude",
  "distanceMeters",
  "link",
  "website",
  "phone",
  "email",
  "price",
  "priceUnit",
  "groupBookingCode",
  "status",
];

const getInitialFormData = (): FormData => ({
  categoryId: null,
  name: "",
  description: "",
  streetAddress: "",
  latitude: null,
  longitude: null,
  distanceKm: "",
  link: "",
  website: "",
  phone: "",
  email: "",
  price: "",
  priceUnit: "",
  groupBookingCode: "",
  photos: [],
  status: STATUSES.ACTIVE,
});

const fromRecord = (record: Accommodation): FormData => ({
  categoryId: record.categoryId || null,
  name: record.name || "",
  description: record.description || "",
  streetAddress: record.streetAddress || "",
  latitude: record.latitude ?? null,
  longitude: record.longitude ?? null,
  distanceKm:
    record.distanceMeters === null || record.distanceMeters === undefined
      ? ""
      : Math.round((record.distanceMeters / 1000) * 10) / 10,
  link: record.link || "",
  website: record.website || "",
  phone: record.phone || "",
  email: record.email || "",
  price: record.price ?? "",
  priceUnit: record.priceUnit || "",
  groupBookingCode: record.groupBookingCode || "",
  photos: (record.images ?? []).map((i) => ({
    uploadedFileId: i.uploadedFileId ?? null,
    url: accommodationImageUrl(i),
  })),
  status: record.status ?? STATUSES.ACTIVE,
});

const serialize = (data: FormData): string => JSON.stringify(data);

const AccommodationEditDrawerSkeleton: React.FC = () => (
  <Stack gap="md">
    <Skeleton height={60} radius="sm" />
    <Skeleton height={60} radius="sm" />
    <Skeleton height={120} radius="sm" />
    <Skeleton height={60} radius="sm" />
  </Stack>
);

export const AccommodationEditDrawer: React.FC<AccommodationEditDrawerProps> = ({
  opened,
  onClose,
  recordId,
  eventId,
  venueLatitude,
  venueLongitude,
  onCreated,
  onUpdated,
}) => {
  const { t } = useTranslation(["events", "common"]);
  // #A4.7b — the price is in the EVENT's currency, shown on the input (sign before, code after); not editable here.
  const currencyAffixes = useCurrencyAffixes();

  const ctx = useOptionalAccommodationsContext();
  const loadFn = ctx?.get;
  const createFn = ctx?.create;
  const updateFn = ctx?.update;
  const creating = ctx?.isCreating ?? false;
  const updating = ctx?.isUpdating ?? false;
  const operationError = ctx?.lastError ?? null;
  const clearOperationError = ctx?.clearError;

  const isEditMode = Boolean(recordId);
  const isSubmitting = creating || updating;

  const loadFnRef = useRef(loadFn);
  loadFnRef.current = loadFn;
  const clearErrRef = useRef(clearOperationError);
  clearErrRef.current = clearOperationError;

  // ── Self-loading ──
  const [record, setRecord] = useState<Accommodation | null>(null);
  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const [loadError, setLoadError] = useState<OpResult | null>(null);

  const showLoadingSkeleton = isLoadingRecord && !record;
  // An edit drawer WITHOUT its record (the read failed) shows the error only — its form still held the last record's
  // values, and Save wrote them over this one (#131 review). Save and ⌘S are off until the record is read.
  const withoutRecord = isEditMode && !record;

  useEffect(() => {
    let isCancelled = false;

    if (opened && isEditMode && recordId && loadFnRef.current) {
      setIsLoadingRecord(true);
      setRecord(null);
      setLoadError(null);

      loadFnRef.current(recordId).then((result) => {
        if (isCancelled) {
          return;
        }

        if (result.didSucceed()) {
          setRecord(result.getDataFirst() as Accommodation);
          setLoadError(null);
        } else {
          setRecord(null);
          setLoadError(result);
        }

        setIsLoadingRecord(false);
      });
    } else if (!opened) {
      setRecord(null);
      setIsLoadingRecord(false);
      setLoadError(null);
    }

    return () => {
      isCancelled = true;
    };
  }, [opened, isEditMode, recordId]);

  // ── Form state ──
  const [formData, setFormData] = useState<FormData>(() => getInitialFormData());
  const initialSnapshotRef = useRef<string>("");
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!opened || showLoadingSkeleton || (isEditMode && !record)) {
      // Not dirty while closed, loading, or without its record (a failed read) — a stale flag from the last visit
      // asked "Discard changes?" over nothing (#131).
      setIsDirty(false);

      return;
    }

    setIsDirty(serialize(formData) !== initialSnapshotRef.current);
  }, [formData, opened, showLoadingSkeleton, isEditMode, record]);

  const effectiveError = loadError || operationError;

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(effectiveError, KNOWN_FORM_FIELDS),
    [effectiveError]
  );

  const formattedGenericError = useMemo(() => formatErrors(genericErrors), [genericErrors]);

  useEffect(() => {
    if (!opened) {
      return;
    }

    if (isEditMode && !record) {
      return;
    }

    const newFormData = record ? fromRecord(record) : getInitialFormData();
    setFormData(newFormData);
    initialSnapshotRef.current = serialize(newFormData);
    setIsDirty(false);
    clearErrRef.current?.();
  }, [opened, record, isEditMode]);

  const setField = useCallback((field: keyof FormData, value: FormData[keyof FormData]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  // The straight-line distance we can suggest from the venue's coordinates and the
  // accommodation's — only when both are known.
  const suggestedMeters = useMemo(
    () => haversineMeters(venueLatitude, venueLongitude, formData.latitude, formData.longitude),
    [venueLatitude, venueLongitude, formData.latitude, formData.longitude]
  );

  const applySuggestedDistance = useCallback(() => {
    if (suggestedMeters === null) {
      return;
    }

    setFormData((prev) => ({ ...prev, distanceKm: Math.round((suggestedMeters / 1000) * 10) / 10 }));
  }, [suggestedMeters]);

  // Place search on the NAME field fills address / website / phone / coordinates. When
  // the picked place gives coordinates and no distance is set yet, offer the venue
  // distance automatically.
  const handleNameChange = useCallback(
    (name: string, placeData: PlaceData | null) => {
      setFormData((prev) => {
        const next: FormData = { ...prev, name };

        if (placeData) {
          if (placeData.website) {
            next.website = placeData.website;
          }
          if (placeData.phone) {
            next.phone = placeData.phone;
          }
          if (placeData.googleMapsUri && !prev.link.trim()) {
            next.link = placeData.googleMapsUri;
          }
          if (placeData.address) {
            const addr = formatAddressFull(placeData.address);
            if (addr) {
              next.streetAddress = addr;
            }
            if (typeof placeData.address.latitude === "number") {
              next.latitude = placeData.address.latitude;
            }
            if (typeof placeData.address.longitude === "number") {
              next.longitude = placeData.address.longitude;
            }
          }

          const auto = haversineMeters(venueLatitude, venueLongitude, next.latitude, next.longitude);
          if (auto !== null && prev.distanceKm === "") {
            next.distanceKm = Math.round((auto / 1000) * 10) / 10;
          }
        }

        return next;
      });
    },
    [venueLatitude, venueLongitude]
  );

  const handleAddressChange = useCallback((address1: string, fullAddress: AddressData | null) => {
    setFormData((prev) => ({
      ...prev,
      streetAddress: fullAddress ? formatAddressFull(fullAddress) : address1,
      latitude:
        fullAddress && typeof fullAddress.latitude === "number"
          ? fullAddress.latitude
          : address1.trim()
            ? prev.latitude
            : null,
      longitude:
        fullAddress && typeof fullAddress.longitude === "number"
          ? fullAddress.longitude
          : address1.trim()
            ? prev.longitude
            : null,
    }));
  }, []);

  // ── Submit ──
  const handleSubmit = useCallback(async () => {
    clearErrRef.current?.();
    setLoadError(null);

    const submitData: AccommodationInput = {
      categoryId: formData.categoryId,
      name: formData.name.trim() || null,
      description: formData.description.trim() || null,
      streetAddress: formData.streetAddress.trim() || null,
      latitude: formData.latitude,
      longitude: formData.longitude,
      distanceMeters: formData.distanceKm === "" ? null : Math.round(Number(formData.distanceKm) * 1000),
      link: formData.link.trim() || null,
      website: formData.website.trim() || null,
      phone: formData.phone.trim() || null,
      email: formData.email.trim() || null,
      price: formData.price === "" ? null : Number(formData.price),
      priceUnit: formData.priceUnit.trim() || null,
      groupBookingCode: formData.groupBookingCode.trim() || null,
      images: formData.photos.map((i) => ({
        uploadedFileId: i.uploadedFileId ?? null,
        // For an uploaded/library image, send only the ref (server resolves the URL);
        // keep the raw URL for stock/URL picks.
        url: i.uploadedFileId ? null : i.url ?? null,
      })),
      status: formData.status,
    };

    if (!isEditMode) {
      submitData.eventId = eventId ?? null;
    }

    let result: OpResult | null = null;
    let isCreate = false;

    if (isEditMode && recordId && updateFn) {
      result = await updateFn(recordId, submitData);
    } else if (!isEditMode && createFn) {
      result = await createFn(submitData);
      isCreate = true;
    }

    if (result?.didSucceed()) {
      setIsDirty(false);
      const saved = result.data ? (result.data as Accommodation[])[0] : null;

      if (saved) {
        if (isCreate) {
          onCreated?.(saved);
        } else {
          onUpdated?.(saved);
        }
      }

      onClose();
    }
  }, [formData, isEditMode, recordId, eventId, createFn, updateFn, onClose, onCreated, onUpdated]);

  useModalShortcuts({
    onConfirm: handleSubmit,
    canConfirm: !isSubmitting && !showLoadingSkeleton && !withoutRecord,
    enabled: opened,
  });

  const drawerTitle = isEditMode
    ? t("events:accommodations.form.editTitle", { defaultValue: "Edit accommodation" })
    : t("events:accommodations.form.addTitle", { defaultValue: "Add accommodation" });

  // ⚠ EVERY key listed in KNOWN_FORM_FIELDS needs somewhere to land. `getFormErrors` puts a KNOWN name in
  // `fields` and NEVER in `genericErrors`, so a listed field with no control SWALLOWS its message entirely —
  // strictly worse than not listing it, because the save just fails with nothing on screen.
  // The coordinate keys have no control of their own here, so they surface on the address block.
  const locationError = fieldErrors.streetAddress || fieldErrors.latitude || fieldErrors.longitude;

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={drawerTitle}
      position="right"
      size="md"
      closable={!isSubmitting}
      loading={isSubmitting}
      errors={formattedGenericError}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      actionType="save-cancel"
      onConfirm={handleSubmit}
      confirmShortcut={SHORTCUT_HINTS.save}
      confirmDisabled={showLoadingSkeleton || withoutRecord}
      isDirty={isDirty}
    >
      {withoutRecord && !showLoadingSkeleton ? null : showLoadingSkeleton ? (
        <AccommodationEditDrawerSkeleton />
      ) : (
        <Stack gap="md">
          <PlaceAutocompleteInput
            label={t("events:accommodations.form.fields.name.label", { defaultValue: "Name" })}
            placeholder={t("events:accommodations.form.fields.name.placeholder", {
              defaultValue: "Search a hotel, or type a name",
            })}
            value={formData.name}
            onChange={handleNameChange}
            placeTypes={ACCOMMODATION_PLACE_TYPES}
            error={fieldErrors.name}
            required
            withinPortal
            size="md"
          />

          <CategorySelect
            group="accommodation"
            exactGroup
            label={t("events:accommodations.form.fields.category.label", { defaultValue: "Type" })}
            description={t("events:accommodations.form.fields.category.description", {
              defaultValue: "Hotel, resort, rental, hostel…",
            })}
            value={formData.categoryId}
            onChange={(id) => setField("categoryId", id)}
            error={fieldErrors.categoryId}
            clearable
            size="md"
          />

          <Input.Wrapper
            label={t("events:accommodations.form.fields.description.label", { defaultValue: "Description" })}
            error={fieldErrors.description}
          >
            <Suspense fallback={<Skeleton height={180} radius="sm" />}>
              <MarkdownField
                value={formData.description}
                onChange={(md) => setField("description", md)}
                placeholder={t("events:accommodations.form.fields.description.placeholder", {
                  defaultValue: "What guests should know about this place…",
                })}
                minHeight={180}
              />
            </Suspense>
          </Input.Wrapper>

          <AddressAutocompleteInput
            label={t("events:accommodations.form.fields.streetAddress.label", { defaultValue: "Address" })}
            placeholder={t("events:accommodations.form.fields.streetAddress.placeholder", {
              defaultValue: "Search an address",
            })}
            value={formData.streetAddress}
            onChange={handleAddressChange}
            error={locationError}
            showLocateButton={false}
            withinPortal
            size="md"
          />

          <div>
            <NumberInput
              label={t("events:accommodations.form.fields.distance.label", { defaultValue: "Distance from venue (km)" })}
              value={formData.distanceKm}
              onChange={(value) => setField("distanceKm", value === "" ? "" : (value as number))}
              error={fieldErrors.distanceMeters}
              min={0}
              decimalScale={1}
              step={0.1}
              size="md"
            />
            {suggestedMeters !== null && (
              <Button
                variant="subtle"
                size="compact-sm"
                mt={4}
                leftSection={<AppIcon iconName="MapPin" size={14} />}
                onClick={applySuggestedDistance}
              >
                {t("events:accommodations.form.fields.distance.suggest", {
                  defaultValue: "Use {{distance}} from venue",
                  distance: formatDistanceFromMeters(suggestedMeters),
                })}
              </Button>
            )}
          </div>

          <TextInput
            label={t("events:accommodations.form.fields.link.label", { defaultValue: "Booking link" })}
            placeholder={t("events:accommodations.form.fields.link.placeholder", {
              defaultValue: "Booking or details URL",
            })}
            value={formData.link}
            onChange={(e) => setField("link", e.currentTarget.value)}
            error={fieldErrors.link}
            size="md"
          />

          <TextInput
            label={t("events:accommodations.form.fields.website.label", { defaultValue: "Website" })}
            placeholder={t("events:accommodations.form.fields.website.placeholder", { defaultValue: "https://…" })}
            value={formData.website}
            onChange={(e) => setField("website", e.currentTarget.value)}
            error={fieldErrors.website}
            size="md"
          />

          <TextInput
            label={t("events:accommodations.form.fields.groupBookingCode.label", { defaultValue: "Group booking code" })}
            placeholder={t("events:accommodations.form.fields.groupBookingCode.placeholder", {
              defaultValue: "e.g. SMITH-WEDDING",
            })}
            description={t("events:accommodations.form.fields.groupBookingCode.description", {
              defaultValue: "The room-block or discount code guests quote when booking.",
            })}
            value={formData.groupBookingCode}
            onChange={(e) => setField("groupBookingCode", e.currentTarget.value)}
            error={fieldErrors.groupBookingCode}
            size="md"
          />

          <TextInput
            label={t("events:accommodations.form.fields.phone.label", { defaultValue: "Phone" })}
            placeholder={t("events:accommodations.form.fields.phone.placeholder", { defaultValue: "+1 555 123 4567" })}
            value={formData.phone}
            onChange={(e) => setField("phone", e.currentTarget.value)}
            error={fieldErrors.phone}
            size="md"
          />

          <TextInput
            label={t("events:accommodations.form.fields.email.label", { defaultValue: "Email" })}
            placeholder={t("events:accommodations.form.fields.email.placeholder", { defaultValue: "hello@example.com" })}
            value={formData.email}
            onChange={(e) => setField("email", e.currentTarget.value)}
            error={fieldErrors.email}
            size="md"
          />

          <NumberInput
            label={t("events:accommodations.form.fields.price.label", { defaultValue: "Price" })}
            value={formData.price}
            onChange={(value) => setField("price", value === "" ? "" : (value as number))}
            error={fieldErrors.price}
            min={0}
            decimalScale={2}
            step={0.01}
            thousandSeparator=","
            size="md"
            {...currencyAffixes}
          />

          <TextInput
            label={t("events:accommodations.form.fields.priceUnit.label", { defaultValue: "Price unit" })}
            placeholder={t("events:accommodations.form.fields.priceUnit.placeholder", { defaultValue: "per night" })}
            value={formData.priceUnit}
            onChange={(e) => setField("priceUnit", e.currentTarget.value)}
            error={fieldErrors.priceUnit}
            size="md"
          />

          <StatusInput
            value={formData.status}
            onChange={(status) => setField("status", status)}
            error={fieldErrors.status}
          />

          <Divider
            label={t("events:accommodations.form.photos.section", { defaultValue: "Room photos" })}
            labelPosition="left"
          />

          <MediaGalleryField
            value={formData.photos}
            onChange={(photos) => setField("photos", photos)}
            contextPath={eventId ? eventContextPath(eventId) : null}
            label={t("events:accommodations.form.photos.label", { defaultValue: "Room photos" })}
            description={t("events:accommodations.form.photos.hint", {
              defaultValue: "The first photo is shown as the main image on your event website.",
            })}
          />
        </Stack>
      )}
    </AppDrawer>
  );
};
